/**
 * wire-commentary-grid.ts — DLITE-3849
 *
 * One declarative call that wires a commentary-enabled AG-Grid into the three
 * drawer integration points, so each grid stops repeating ~55 lines of
 * identical boilerplate:
 *
 *   - follow$        → focus the clicked entity's row (or filter to reveal it);
 *   - entitySearch   → contribute this grid's loaded rows to the type-ahead;
 *   - pickEntities   → contribute this grid's selection to "Pick from grid".
 *
 * The follow-listener idempotency, the ReplaySubject(1) replay-dedupe, the
 * takeUntil teardown, and the source unregister are all handled here. Each
 * grid supplies only what is genuinely grid-specific: its panel key, entity
 * type, id column, and the two row→ref mappers.
 *
 * Call once the grid has commentary access, at most once per component
 * instance — either via a one-shot policy pipe (takeWhile-inclusive on the
 * first granting emission) or an idempotency guard.
 */
import { GridApi } from '@ag-grid-community/core'
import { ToastService } from '@northfork/ng-basics'
import { Observable } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { CommentaryFollowService } from '../services/commentary-follow.service'
import { CommentaryEntitySearchService } from '../services/commentary-entity-search.service'
import { CommentaryGridSelectionService } from '../services/commentary-grid-selection.service'
import {
  CommentaryEntityRef,
  CommentaryEntityType,
  CommentaryFollowTarget,
} from '../types/thread-jump'
import { defaultIdFilter, focusGridRow, IdFilterModel } from './focus-grid-row'
import { followNotice, FollowNoticeReason } from './follow-notice'
import { PendingReveal, revealGridRow } from './reveal-grid-row'

export interface CommentaryGridWiring {
  follow: CommentaryFollowService
  search: CommentaryEntitySearchService
  selection: CommentaryGridSelectionService
  /** Registration key for this grid's search/selection sources. */
  panelKey: string
  /** Entity type this grid owns; follow + search/pick are constrained to it. */
  entityType: CommentaryEntityType
  /** AG-Grid column id of this entity's id, for the follow row-focus filter. */
  idColId: string
  /**
   * Whether the entity id equals the grid's row-node id (default true). Grids
   * whose row-node id differs from the entity id (e.g. a synthetic rowId)
   * pass false so focusGridRow skips the node lookup and goes straight to the
   * filter. Mirrors the focusGridRow argument of the same name.
   */
  hasRowNodeId?: boolean
  /** Lazily read the grid api (it may not exist until the grid mounts). */
  getApi: () => GridApi | null | undefined
  /** Commentary-access gate, re-checked on every follow event. */
  hasAccess: () => boolean
  /** Emits on grid teardown — the component's `destroySubject$`. */
  destroy$: Observable<unknown>
  /** Currently-loaded rows as refs (the entitySearch corpus). */
  loadedRefs: () => CommentaryEntityRef[]
  /** Current selection as refs (the pickEntities source). */
  selectedRefs: () => CommentaryEntityRef[]
  /**
   * Reveal an off-screen target by filtering when the row isn't loaded
   * (default true). Set false for ambiguous types: an `account` chip maps to
   * both the Accounts and My Accounts grids, so the grid that lacks the row
   * would otherwise filter itself to an empty result — those grids focus only
   * when they already hold it.
   */
  filterWhenMissing?: boolean
  /**
   * Builds the reveal filter-model entry for `idColId` when the row isn't
   * loaded. Defaults to a text `equals` filter (correct for string id
   * columns). A numeric id column (account-list's `accountId`, external
   * transactions' `extTranId`) MUST pass a number filter — a text/equals model
   * on a number column matches nothing, so the grid filters itself to zero
   * rows. Mirrors focusGridRow's argument.
   */
  idFilter?: IdFilterModel
  /**
   * Checks the reveal filter's answer: when nothing matched or the load
   * failed, the grid's previous filters are put back and the user is told
   * why. Omit to keep the plain reveal filter; Account List does, so account
   * links behave as they always have. Server-side grids only.
   */
  toast?: ToastService
  /**
   * Accounts loaded in this grid's session. A target filed on another account
   * is ignored: onFollowEntity is switching sessions for it and this grid is
   * about to be replaced, so its empty answer would be a false "not found".
   * The replacement grid doesn't receive the follow either: it wires before
   * its api exists, and a target that arrives without an api is dropped.
   */
  sessionAccountIds?: () => readonly number[] | null | undefined
  /** False for an id the reveal filter can't match, e.g. a stored UUID on a numeric column. */
  canFilter?: (id: string) => boolean
  /** For grids that can answer before their session cache is complete. Mirrors revealGridRow. */
  isComplete?: () => boolean
  /** Other column filters to apply with the reveal filter. Mirrors revealGridRow's `alongside`. */
  revealAlongside?: () => Record<string, unknown>
  /**
   * Replaces the reveal filter when the row isn't loaded. For a client-side
   * grid, whose row lookup already searched every row it holds, a filter
   * could only empty it.
   */
  onMissing?: (target: CommentaryFollowTarget) => void
}

export function wireCommentaryGrid(w: CommentaryGridWiring): void {
  const filterWhenMissing = w.filterWhenMissing ?? true
  w.follow.start() // idempotent — service guards re-entry

  let lastTarget: CommentaryEntityRef | null = null
  let pending: PendingReveal | null = null
  w.follow.follow$.pipe(takeUntil(w.destroy$)).subscribe(target => {
    if (!w.hasAccess()) return
    if (target.type !== w.entityType) return
    const api = w.getApi()
    if (api == null) return
    // follow$ is a ReplaySubject(1): dedupe the replayed target by reference
    // so a remount (tab switch) doesn't re-focus a stale row.
    if (target === lastTarget) return
    lastTarget = target
    if (!inSession(w, target)) return
    const hasRowNodeId = w.hasRowNodeId ?? true

    if (w.onMissing) {
      if (!focusGridRow(api, w.idColId, target.id, hasRowNodeId, false)) {
        w.onMissing(target)
      }
      return
    }
    const toast = w.toast
    if (toast == null || !filterWhenMissing) {
      focusGridRow(
        api,
        w.idColId,
        target.id,
        hasRowNodeId,
        filterWhenMissing,
        w.idFilter,
      )
      return
    }
    const notify = (reason: FollowNoticeReason) => {
      const { title, message } = followNotice(target, reason)
      toast.warning(message, undefined, title)
    }
    const superseded = pending?.isPending() ? pending : null
    pending = null
    if (w.canFilter?.(target.id) === false) {
      if (focusGridRow(api, w.idColId, target.id, hasRowNodeId, false)) {
        superseded?.cancel()
      } else {
        superseded?.withdraw()
        notify('olderReference')
      }
      return
    }
    superseded?.cancel()

    const reveal = revealGridRow(api, w.idColId, target.id, {
      hasRowNodeId,
      idFilter: w.idFilter ?? defaultIdFilter,
      alongside: w.revealAlongside?.(),
      previousFilterModel: superseded?.previousFilterModel,
      isComplete: w.isComplete,
    })
    pending = reveal
    void reveal.outcome.then(outcome => {
      if (pending === reveal) pending = null
      if (outcome === 'notFound' || outcome === 'loadFailed') notify(outcome)
      if (outcome === 'notReady') notify('stillLoading')
    })
  })

  w.search.register(w.panelKey, w.loadedRefs)
  w.selection.register(w.panelKey, w.selectedRefs)
  w.destroy$.pipe(take(1)).subscribe(() => {
    pending?.cancel()
    w.search.unregister(w.panelKey)
    w.selection.unregister(w.panelKey)
  })
}

function inSession(
  w: CommentaryGridWiring,
  target: CommentaryFollowTarget,
): boolean {
  if (w.sessionAccountIds == null || target.threadAccountId == null) return true
  const accountIds = w.sessionAccountIds()
  return (
    accountIds == null || accountIds.includes(Number(target.threadAccountId))
  )
}
